import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EmailIntegrationAccountRow } from '@elva/ui';
import { keys } from '@/api/queryKeys';
import { emailApi } from '@/features/email/api/emailApi';
import type { EmailAccount } from '@/features/email/api/types';
import { settingsApi } from '../api/settingsApi';
import {
  clearEmailOauthSession,
  readEmailOauthSession,
  readOAuthRedirectUrl,
  rememberEmailOauthSession,
} from '../api/emailOauth';

function toIntegrationRow(account: EmailAccount): EmailIntegrationAccountRow {
  return {
    id: account.id,
    emailAddress: account.email_address,
    provider: account.provider,
    status: account.status,
    lastSyncAt: account.last_sync_at,
  };
}

async function startGmailOauth(): Promise<string> {
  try {
    const nativeStart = await emailApi.startAccountOauth('gmail');
    const nativeUrl = readOAuthRedirectUrl(nativeStart);
    if (nativeUrl) {
      rememberEmailOauthSession('gmail', 'accounts');
      return nativeUrl;
    }
  } catch {
    // Fall back to settings OAuth start.
  }

  const settingsStart = await settingsApi.startEmailIntegrationOauth('gmail');
  const settingsUrl = readOAuthRedirectUrl(settingsStart);
  if (settingsUrl) {
    rememberEmailOauthSession('gmail', 'settings');
    return settingsUrl;
  }

  throw new Error('OAuth start did not return a redirect URL.');
}

export async function completeEmailOauth(payload: { code: string; state: string }) {
  const { provider, flow } = readEmailOauthSession();
  const body = { provider, code: payload.code, state: payload.state };

  const completeViaAccounts = () => emailApi.completeAccountOauth(body);
  const completeViaSettings = () => settingsApi.completeEmailIntegrationOauth(body);

  if (flow === 'settings') {
    try {
      await completeViaSettings();
      return;
    } catch {
      await completeViaAccounts();
      return;
    }
  }

  try {
    await completeViaAccounts();
  } catch {
    await completeViaSettings();
  }
}

export function useConnectEmailMailbox() {
  return useMutation({
    mutationFn: async () => {
      const url = await startGmailOauth();
      window.location.assign(url);
    },
  });
}

export function useCompleteEmailOauth() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: completeEmailOauth,
    onSuccess: () => {
      clearEmailOauthSession();
      void queryClient.invalidateQueries({ queryKey: keys.email.accounts });
      void queryClient.invalidateQueries({ queryKey: keys.email.mailAccounts });
      void queryClient.invalidateQueries({ queryKey: keys.settings.emailInteg });
      void queryClient.invalidateQueries({ queryKey: keys.email.messages() });
    },
  });
}

export function mapEmailAccountsToIntegrationRows(
  accounts: EmailAccount[],
): EmailIntegrationAccountRow[] {
  return accounts.map(toIntegrationRow);
}
